import type {AssistantPanelTipTone} from '@chekinapp/ui';
import type {ReactNode} from 'react';
import type {TFunction} from 'i18next';
import type {CaptureMode} from '@chekin/shared';
import type {ContactsVerificationState} from 'hooks/iv/useContactsVerificationStore';
import type {KeysState} from 'components/RemoteAccess/useKeysState';
import type {ActionableConditionKind} from 'components/RemoteAccess/lockConditions';

export enum VelaScene {
  GuestRegistration = 'guestRegistration',
  GuestForm = 'guestForm',
  GuestRegistering = 'guestRegistering',
  GuestRegistered = 'guestRegistered',
  GuestRegistrationFailed = 'guestRegistrationFailed',
  IVStart = 'ivStart',
  IVDocumentSelection = 'ivDocumentSelection',
  IVFront = 'ivFront',
  IVBack = 'ivBack',
  IVSelfie = 'ivSelfie',
  IVContacts = 'ivContacts',
  IVUnavailable = 'ivUnavailable',
  IVQRScan = 'ivQrScan',
  RemoteAccess = 'remoteAccess',
  Taxes = 'taxes',
  Deposit = 'deposit',
  Payments = 'payments',
  OrderHistory = 'orderHistory',
  Upselling = 'upselling',
  UpsellingOffer = 'upsellingOffer',
  UpsellingRequests = 'upsellingRequests',
  Chat = 'chat',
  ChatVerify = 'chatVerify',
  ChatVerifyError = 'chatVerifyError',
  ChatDisabled = 'chatDisabled',
  ChatNotRegistered = 'chatNotRegistered',
  Esim = 'esim',
  Checkout = 'checkout',
  CheckoutRate = 'checkoutRate',
  GuidebookDetail = 'guidebookDetail',
  TravelGuide = 'travelGuide',
}

export type VelaSectionId =
  | 'nextGuest'
  | 'askVela'
  | 'whyVerify'
  | 'whyYouCantStart'
  | 'quickActions'
  | 'photoTips'
  | 'findingTheCode'
  | 'aboutYourCode'
  | 'wrongCode'
  | 'goodToKnow'
  | 'aboutPayLater'
  | 'readingYourHistory'
  | 'isThisSecure'
  | 'whileYouWait'
  | 'twoWaysToBook'
  | 'requestStatuses'
  | 'privacy';

export type VelaCartFacts = {
  hasHolds: boolean;
  hasAuthorisations: boolean;
  hasPayLater: boolean;
  hasReceipts?: boolean;
};

export type VelaTaxFacts = {
  rate: string;
  perGuest: string;
  isPerDay: boolean;
  exemptCount: number;
  unregisteredCount: number;
  canDeclareExemptions: boolean;
};

export type VelaDepositFacts = {
  hasDeposit: boolean;
  hasProtection: boolean;
  isRetention: boolean;
  depositAmount: string;
  protectionAmount: string;
  protectionLimit: string;
  depositFee: string;
};

export type VelaStayOffers = {
  earlyCheckInId: string | null;
  lateCheckOutId: string | null;
};

export type VelaCard = {
  id: string;
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  tone?: AssistantPanelTipTone;
  priority?: number;
};

export type VelaContext = {
  scene: VelaScene;
  isIVEnabled: boolean;
  isIVCompleted: boolean;
  biomatch: boolean;
  isRegistrationComplete: boolean;
  countryCode: string;
  isDataPrefilled: boolean;
  hasDocumentScan: boolean;
  hasOptionalFields: boolean;
  isChatEnabled: boolean;
  isChatAttachmentsEnabled: boolean;
  isFaqEnabled: boolean;
  hasGuidebooks: boolean;
  guestCount: {adults: number; minors: number};
  totalGuests: number;
  pendingGuests: number;
  registeredFirstName: string;
  canShareRegistration: boolean;
  stayTimes: {checkIn?: string; checkOut?: string};
  stayOffers: VelaStayOffers;
  isRemoteAccessEnabled: boolean;
  captureMode: CaptureMode;
  contacts: ContactsVerificationState;
  keysState: KeysState;
  blockingKeyCondition: ActionableConditionKind | null;
  hasCodeKeys: boolean;
  hasLinkKeys: boolean;
  deposit: VelaDepositFacts;
  taxes: VelaTaxFacts;
  upsell: {isOnRequest: boolean; isLuggage: boolean; hasStorage: boolean};
  cart: VelaCartFacts;
};

export type VelaRule = {
  id: string;
  section: VelaSectionId;
  scenes: VelaScene[];
  priority?: number;
  evaluate: (ctx: VelaContext, t: TFunction) => VelaCard | null;
};

export type VelaCardSection = {
  id: VelaSectionId;
  label: string;
  cards: VelaCard[];
};

export type VelaContent = {
  sections: VelaCardSection[];
};

const isCameraCapture = (ctx: VelaContext) => ctx.captureMode === 'camera';

const getPhotoTipsLabelKey = (ctx: VelaContext) => {
  if (ctx.scene === VelaScene.IVSelfie) {
    return isCameraCapture(ctx)
      ? 'section.selfie_tips_camera'
      : 'section.selfie_tips_gallery';
  }
  return isCameraCapture(ctx)
    ? 'section.photo_tips_camera'
    : 'section.photo_tips_gallery';
};

export type VelaSectionConfig = {
  id: VelaSectionId;
  labelKey: string | ((ctx: VelaContext) => string);
  cap: number;
};

export const SECTION_ORDER: VelaSectionConfig[] = [
  {id: 'nextGuest', labelKey: 'section.quick_actions', cap: 1},
  {id: 'askVela', labelKey: 'section.ask_vela', cap: 5},
  {id: 'whyVerify', labelKey: 'section.why_we_verify', cap: 2},
  {id: 'whyYouCantStart', labelKey: 'section.why_you_cant_start', cap: 2},
  {id: 'findingTheCode', labelKey: 'section.finding_the_code', cap: 2},
  {id: 'aboutYourCode', labelKey: 'section.about_your_code', cap: 2},
  {id: 'wrongCode', labelKey: 'section.wrong_code', cap: 2},
  {id: 'twoWaysToBook', labelKey: 'section.two_ways_to_book', cap: 3},
  {id: 'requestStatuses', labelKey: 'section.what_the_statuses_mean', cap: 3},
  {id: 'aboutPayLater', labelKey: 'section.about_pay_later', cap: 2},
  {id: 'readingYourHistory', labelKey: 'section.reading_your_history', cap: 3},
  {id: 'isThisSecure', labelKey: 'section.is_this_secure', cap: 3},
  {id: 'whileYouWait', labelKey: 'section.while_you_wait', cap: 1},
  {id: 'goodToKnow', labelKey: 'section.good_to_know', cap: 5},
  {id: 'photoTips', labelKey: getPhotoTipsLabelKey, cap: 4},
  {id: 'quickActions', labelKey: 'section.quick_actions', cap: 3},
  {id: 'privacy', labelKey: 'section.privacy', cap: 1},
];
